export type ShardStatus =
  | 'TODO'
  | 'READY'
  | 'IN_PROGRESS'
  | 'DONE'
  | 'REVIEW'
  | 'BLOCKED'
  // #343: a task prism replaced with smaller ones. Finished with, but not a
  // completion: it sits in Done with its own badge, counts in nobody's
  // progress, and satisfies nobody's dependency.
  | 'DECOMPOSED'
  | 'FINAL_REVIEW'
  | 'FINAL_APPROVED'
  | 'AWAITING_HUMAN_ACTION'
  // Approval gate statuses
  | 'DESIGN_PENDING'
  | 'DESIGN_APPROVED'
  | 'DESIGN_REJECTED'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'

export interface Shard {
  id: string
  title: string
  status: ShardStatus
  assigned_worker?: string | null
  created_at?: string
  updated_at?: string
  type?: string
  priority?: string
  spec?: string
  assignee?: string
  parent_id?: string | null
  depends_on?: string[]
  files?: string[] // Files this task is expected to touch (issue #41 — overlap guard)
  scratchpad?: string | null
}

export type KanbanColumn = 'Backlog' | 'Blocked' | 'In Progress' | 'Review' | 'Done'

export const STATUS_TO_COLUMN: Record<ShardStatus, KanbanColumn> = {
  TODO: 'Backlog',
  READY: 'Backlog',
  IN_PROGRESS: 'In Progress',
  REVIEW: 'Review',
  FINAL_REVIEW: 'Review',
  FINAL_APPROVED: 'Review',
  DONE: 'Done',
  DECOMPOSED: 'Done',
  BLOCKED: 'Blocked',
  // Approval gate statuses - show in Review column
  DESIGN_PENDING: 'Review',
  DESIGN_APPROVED: 'In Progress',
  DESIGN_REJECTED: 'Blocked',
  PENDING_APPROVAL: 'Review',
  APPROVED: 'Done',
  REJECTED: 'Blocked',
  AWAITING_HUMAN_ACTION: 'Blocked',
}

// Statuses that require human approval
export const APPROVAL_PENDING_STATUSES: ShardStatus[] = ['DESIGN_PENDING', 'PENDING_APPROVAL', 'AWAITING_HUMAN_ACTION']

// Helper to check if a shard needs approval
export function isApprovalPending(status: ShardStatus): boolean {
  return APPROVAL_PENDING_STATUSES.includes(status)
}

// Log types for unified log view
export interface LogEntry {
  id: string
  timestamp: string
  source: 'activity' | string  // 'activity' or persona name
  worker?: string
  persona?: string
  eventType?: string
  message: string
}

export interface LogFilters {
  workers: string[]
  eventTypes: string[]
  timeRange: {
    start: Date | null
    end: Date | null
  }
  search: string
}

// Time range presets for quick filtering
export type TimeRangePreset = 'all' | 'last5m' | 'last30m' | 'last1h' | 'last24h'

export const TIME_RANGE_PRESETS: Record<TimeRangePreset, { label: string; getRange: () => { start: Date | null; end: Date | null } }> = {
  all: { label: 'All Time', getRange: () => ({ start: null, end: null }) },
  last5m: { label: 'Last 5 min', getRange: () => ({ start: new Date(Date.now() - 5 * 60 * 1000), end: null }) },
  last30m: { label: 'Last 30 min', getRange: () => ({ start: new Date(Date.now() - 30 * 60 * 1000), end: null }) },
  last1h: { label: 'Last hour', getRange: () => ({ start: new Date(Date.now() - 60 * 60 * 1000), end: null }) },
  last24h: { label: 'Last 24h', getRange: () => ({ start: new Date(Date.now() - 24 * 60 * 60 * 1000), end: null }) },
}

/** A task that is genuinely complete, for progress and dependency readers. */
export const COMPLETED_STATUSES: ShardStatus[] = ['DONE', 'APPROVED', 'FINAL_APPROVED']

/**
 * Percentage of a spec's tasks that are complete. A DECOMPOSED task is out of
 * both numerator and denominator: its work is counted through its
 * replacements, which are tasks of the same spec.
 */
export function specProgress(tasks: Pick<Shard, 'status'>[]): number {
  const counted = tasks.filter(t => t.status !== 'DECOMPOSED')
  if (counted.length === 0) return 0
  const done = counted.filter(t => COMPLETED_STATUSES.includes(t.status)).length
  return Math.round((done / counted.length) * 100)
}
